import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, inject, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UploadFileControl, UploadFileModel } from '@portal/upload-file/data-access';
import { Helper } from '@portal/util';

@Component({
  selector: 'portal-upload-file-control',
  standalone: true,
  imports: [],
  template: `
    <input
      #input
      (change)="input.files && onFileSelected(input.files)"
      (blur)="onTouched()"
      [accept]="acceptMineTypes"
      [id]="id"
      [multiple]="multiple"
      [name]="name"
      style="display: none"
      type="file"
    />
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ControlComponent),
    },
  ],
})
export class ControlComponent implements ControlValueAccessor {
  @ViewChild('input', { static: true }) private readonly inputRef!: ElementRef<HTMLInputElement>;
  private filesSelected: UploadFileControl = new Map();
  private readonly cdr = inject(ChangeDetectorRef);
  @Input() id?: string;
  @Input() name?: string;
  @Input() multiple? = false;
  @Input() acceptMineTypes: string[] = [];

  writeValue(value?: UploadFileControl): void {
    this.filesSelected = value || new Map();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  protected onTouched: () => void = () => {};

  protected onFileSelected(fileList: FileList): void {
    Array.from(fileList).forEach(file => {
      const ext = `.${Helper.getFileExtension(file.name)}`;
      if (this.acceptMineTypes.some(accept => accept === ext?.toLowerCase())) {
        const id = Helper.randomId(4);
        this.filesSelected.set(id, UploadFileModel.fromJson({ id, file, fileName: file.name }));
      }
    });

    this.inputRef.nativeElement.value = ''; // need to make sure input always change when selected previous files

    this.onChange(this.filesSelected);
    this.onTouched();
  }

  protected onChange: (newValue: UploadFileControl) => void = () => {};
}
